import {
  combineReducers,
  configureStore,
  ThunkAction,
  UnknownAction,
} from '@reduxjs/toolkit';
import authSlice, {
  INITIAL_STATE as authInitialState,
} from './slices/auth/authSlice';
import apiAccess from '../api/apiAccess';
import taxSlice from './slices/tax/taxSlice';
import userLicensesSlice from './slices/userLicenses/userLicensesSlice';
import applySlice from './slices/apply/applySlice';
import configSlice from './slices/config/configSlice';
import dashboardSlice from './slices/dashboard/dashboardSlice';
import paymentSlice from './slices/payment/paymentSlice';
import licenseViewSlice from './slices/licenseView/licenseViewSlice';
import globalsSlice from './slices/globals/globalsSlice';
import flowSlice from './slices/flow/flowSlice';
import updateSlice from './slices/update/updateSlice';
import renewSlice from './slices/renew/renewSlice';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import adminUpdateSlice from './slices/adminUpdate/adminUpdateSlice';
import taxCertificateSlice from './slices/taxCertificate/taxCertificateSlice';
import { useDispatch } from 'react-redux';

const portalKey = window.location.pathname.split('/')[1] || 'root';

const persistConfig = {
  key: `root:${portalKey}`,
  storage,
  blacklist: ['userLicenses'],
};

const rootReducer = {
  adminUpdate: adminUpdateSlice,
  apply: applySlice,
  taxCertificate: taxCertificateSlice,
  auth: authSlice,
  config: configSlice,
  dashboard: dashboardSlice,
  flow: flowSlice,
  globals: globalsSlice,
  licenseView: licenseViewSlice,
  payment: paymentSlice,
  renew: renewSlice,
  tax: taxSlice,
  update: updateSlice,
  userLicenses: userLicensesSlice,
};

const combinedReducers = combineReducers(rootReducer);

// Create a root reducer that handles the reset action
const rootReducerWithReset = (state: any, action: any) => {
  if (action.type === 'resetRegSystem') {
    const resetedState = {
      config: state.config,
      globals: state.globals,
      auth: {
        ...authInitialState,
        isWorkFailure: state.auth.isWorkFailure,
        isWorkStarted: state.auth.isWorkStarted,
        isWorkSuccess: state.auth.isWorkSuccess,
        workErrorMsg: state.auth.workErrorMsg,
      },
    };

    return combinedReducers(resetedState, action);
  }
  return combinedReducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducerWithReset);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: apiAccess,
      },
      serializableCheck: false,
    }),
  devTools: true,
});

export const setupStore = (preloadedState: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducerWithReset,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: apiAccess,
        },
        serializableCheck: false,
      }),
    devTools: true,
    preloadedState,
  });
};

export const persistor = persistStore(store);

export const useAppDispatch: () => AppDispatch = useDispatch;

export type RootState = ReturnType<typeof combinedReducers>;
export type AppStore = typeof store;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void, T = unknown> = ThunkAction<
  ReturnType,
  RootState,
  T,
  UnknownAction
>;
export default store;
